/**
 * Simple auth context — stores JWT in localStorage, exposes user info.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("auth_token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((t) => {
    if (t) {
      localStorage.setItem("auth_token", t);
      axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    } else {
      localStorage.removeItem("auth_token");
      delete axios.defaults.headers.common["Authorization"];
    }
    setToken(t);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
    } catch (e) {
      setUser(null);
      applyToken(null);
    } finally { setLoading(false); }
  }, [applyToken]);

  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    refresh();
  }, [token, refresh]);

  const signup = async (email, password) => {
    const res = await api.signup(email, password);
    applyToken(res.token);
    setUser(res.user);
    return res.user;
  };

  const login = async (email, password) => {
    const res = await api.login(email, password);
    applyToken(res.token);
    setUser(res.user);
    return res.user;
  };

  const logout = () => {
    applyToken(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, token, loading, signup, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
